import { nanoid } from 'nanoid'
import * as Sentry from '@sentry/browser'
import { initUploadBatch } from '@/uppy-file-upload/fetch/init-upload'
import { UppyFileUploadActions } from '@/uppy-file-upload/store/slice'
import { uploadInitializedUserFileBlob } from '@/uppy-file-upload/utils/upload-initialized-user-file-blob'
import type { AppDispatch } from '@/store/store'

type SaveResult = { fileId: string; filename: string }

export async function uploadBlobToUserFiles(blob: Blob, filename: string, folderId: string, dispatch?: AppDispatch): Promise<SaveResult> {
  const processedFilename = filename.trim()
  const filenameWithExt = processedFilename.toLowerCase().endsWith('.docx') ? processedFilename : `${processedFilename}.docx`
  const clientTmpId = nanoid()

  dispatch?.(UppyFileUploadActions.fileQueued({ uppyFileId: clientTmpId, clientTmpId, filename: filenameWithExt, fileSize: blob.size, folderId }))
  dispatch?.(UppyFileUploadActions.fileInitializing({ clientTmpId }))

  const initResponse = await initUploadBatch([{ clientTmpId, filename: filenameWithExt, folderId }])
  const initializedFile = initResponse.files.find((f) => f.client_tmp_id === clientTmpId)

  if (!initializedFile) {
    dispatch?.(UppyFileUploadActions.uploadFailed({ clientTmpId, error: 'Unable to initialize upload for this file' }))
    throw new Error('Unable to initialize upload for this file')
  }

  dispatch?.(UppyFileUploadActions.fileInitialized({ clientTmpId, serverFileId: initializedFile.file_id, storagePath: initializedFile.storage_path }))
  dispatch?.(UppyFileUploadActions.uploadStarted({ clientTmpId }))

  try {
    await uploadInitializedUserFileBlob({
      clientTmpId,
      uploadTarget: initializedFile,
      blob,
      onProgress: (progress) => {
        dispatch?.(UppyFileUploadActions.uploadProgress({ clientTmpId, progress: Math.round(progress) }))
      },
    })
  } catch (error) {
    dispatch?.(UppyFileUploadActions.uploadFailed({ clientTmpId, error: error instanceof Error ? error.message : 'Upload failed' }))
    Sentry.captureException(error, { extra: { folderId, filename: filenameWithExt } })
    throw error
  }

  dispatch?.(UppyFileUploadActions.uploadCompleted({ clientTmpId }))

  return { fileId: initializedFile.file_id, filename: filenameWithExt }
}
